import React, {useEffect, useRef} from 'react'
import {i18n} from "@lingui/core"

const NEW_PLATFORM_URL = "https://app.unisoft.world"

const MESSAGES = {
    fr: {
        title: "Ancienne plateforme — non maintenue",
        text: "Toute demande de support ou d'aide concernant cette plateforme sera ignorée. Rendez-vous sur la nouvelle version, maintenue par le support.",
        textShort: "Toute demande de support concernant cette plateforme sera ignorée."
    },
    en: {
        title: "Legacy platform — no longer maintained",
        text: "Any support or help request concerning this platform will be ignored. Please move to the new version, maintained by support.",
        textShort: "Any support request concerning this platform will be ignored."
    },
    he: {
        title: "פלטפורמה ישנה — אינה מתוחזקת",
        text: "כל פנייה לתמיכה או לסיוע בנוגע לפלטפורמה זו תיוותר ללא מענה. יש לעבור לגרסה החדשה, המתוחזקת על ידי התמיכה.",
        textShort: "כל פנייה לתמיכה בנוגע לפלטפורמה זו תיוותר ללא מענה."
    }
}

const CSS = `
:root{--deprecation-banner-h:0px}
.deprecation-banner{background:#A32D2D;color:#fff;padding:12px 20px;line-height:1.4;position:relative;z-index:1001;
display:grid;grid-template-columns:auto minmax(0,1fr) auto;
grid-template-areas:"icon title cta" "icon text cta";
column-gap:16px;align-items:center}
.deprecation-banner__icon{grid-area:icon;width:26px;height:26px}
.deprecation-banner__title{grid-area:title;font-size:15px;font-weight:600;letter-spacing:.2px}
.deprecation-banner__text{grid-area:text;font-size:13px;line-height:1.5;color:#F7C1C1;margin-top:2px}
.deprecation-banner__text--short{display:none}
.deprecation-banner__cta{grid-area:cta;background:#fff;color:#501313;text-decoration:none;font-size:13px;
font-weight:600;padding:9px 16px;border-radius:6px;display:inline-flex;align-items:center;gap:7px;white-space:nowrap}
.deprecation-banner__cta:hover,.deprecation-banner__cta:focus{background:#FCEBEB;color:#501313;text-decoration:none}

body .app-header{top:var(--deprecation-banner-h)}
body .top-nav{top:calc(70px + var(--deprecation-banner-h))}
body .side-nav{top:calc(70px + var(--deprecation-banner-h));height:calc(100vh - 70px - var(--deprecation-banner-h))}
body .ant-affix{top:calc(70px + var(--deprecation-banner-h))!important}
body .auth-container{height:calc(100vh - var(--deprecation-banner-h))}
body .app-content{min-height:calc(100vh - 130px - var(--deprecation-banner-h))}
body .app-content.layout-top-nav{min-height:calc(100vh - 130px - 55px - var(--deprecation-banner-h))}
body .inner-app-layout > .main-content{min-height:calc(100vh - 182px - var(--deprecation-banner-h))}

@media (max-width:767px){
.deprecation-banner{padding:12px 14px;grid-template-columns:auto minmax(0,1fr);
grid-template-areas:"icon title" "text text" "cta cta";column-gap:8px}
.deprecation-banner__icon{width:18px;height:18px}
.deprecation-banner__title{font-size:13px;line-height:1.3}
.deprecation-banner__text{font-size:12px;margin-top:6px}
.deprecation-banner__text--long{display:none}
.deprecation-banner__text--short{display:block}
.deprecation-banner__cta{margin-top:10px;font-size:12px;padding:8px 12px;justify-content:center}
}
`

const WarningIcon = () => (
    <svg className="deprecation-banner__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 9v4"/>
        <path d="M10.363 3.591 2.257 17.125a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636-2.871L13.637 3.59a1.914 1.914 0 0 0-3.274 0z"/>
        <path d="M12 16h.01"/>
    </svg>
)

const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12h14"/>
        <path d="m13 18 6-6-6-6"/>
    </svg>
)

const DeprecationBanner = () => {

    const ref = useRef(null)

    const locale = MESSAGES[i18n.locale] ? i18n.locale : "fr"
    const msg = MESSAGES[locale]

    useEffect(() => {
        const node = ref.current
        if (!node) return

        const publishHeight = () => {
            document.documentElement.style.setProperty("--deprecation-banner-h", `${node.offsetHeight}px`)
        }

        publishHeight()

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", publishHeight)
            return () => {
                window.removeEventListener("resize", publishHeight)
                document.documentElement.style.setProperty("--deprecation-banner-h", "0px")
            }
        }

        const observer = new ResizeObserver(publishHeight)
        observer.observe(node)
        return () => {
            observer.disconnect()
            document.documentElement.style.setProperty("--deprecation-banner-h", "0px")
        }
    }, [])

    return (
        <>
            <style>{CSS}</style>
            <div className="deprecation-banner" role="alert" ref={ref} dir={locale === "he" ? "rtl" : "ltr"}>
                <WarningIcon/>
                <div className="deprecation-banner__title">{msg.title}</div>
                <div className="deprecation-banner__text deprecation-banner__text--long">{msg.text}</div>
                <div className="deprecation-banner__text deprecation-banner__text--short">{msg.textShort}</div>
                <a className="deprecation-banner__cta" href={NEW_PLATFORM_URL} target="_blank" rel="noopener noreferrer">
                    app.unisoft.world <ArrowIcon/>
                </a>
            </div>
        </>
    )
}

export default DeprecationBanner
